/**
 * Halloween cross-stitch patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenCrossStitchPatternsPack: ToilePack = {
    slug: 'halloween-cross-stitch-patterns',
    category: 'season',
    name: 'Halloween cross-stitch patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'pumpkin', label: 'Pumpkin chart', subject: 'A carved pumpkin'},
        {key: 'ghost', label: 'Ghost chart', subject: 'A little ghost'},
        {key: 'hat', label: "Witch's hat", subject: "A witch's hat"},
    ],
    styles: [
        {key: 'chunky', label: 'Chunky chart', style: 'a chunky low-count cross-stitch chart, large bold stitch symbols on a coarse grid', palette: 'Black symbols on white grid', framing: 'Centered on the page with a wide white margin, a symbol key in the corner'},
        {key: 'fine', label: 'Fine chart', style: 'a fine high-count cross-stitch chart, small detailed stitch symbols on a dense grid', palette: 'Black symbols on white grid', framing: 'Filling the page within a moderate white margin, a symbol key in the corner'},
        {key: 'mini', label: 'Mini chart', style: 'a small mini quick-stitch chart, a compact symbol grid', palette: 'Black symbols on white grid', framing: 'Centered on the page with a wide white margin, a symbol key in the corner'},
    ],
    directions: ['a bookmark-sized chart', 'as a repeating border', 'a black cat instead'],
    directionHint: 'try: a bookmark-sized chart',
    seo: {
        title: "Halloween Cross Stitch Pattern: Pumpkin, Ghost, Witch's Hat",
        metaDescription: "Nine halloween cross stitch charts — a pumpkin, a ghost, a witch's hat — gridded with symbols, black on white, three styles.",
        h1: "Halloween cross stitch pattern: pumpkin, ghost, witch's hat",
        tagline: 'Nine charts, three October subjects, three styles each.',
        intro: "Gridded cross stitch charts for the hoop or aida cloth: a pumpkin, a ghost, a witch's hat, each laid out on a stitch grid with symbols marking every square. Ask for a black cat, a bat, or your own subject, and the same three styles carry it.",
        useCases: [
            'a cross stitch hoop gift',
            'a Halloween craft fair item',
            'a handmade Halloween card insert',
            'a beginner cross stitch practice project',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: "Nine cross stitch charts — a pumpkin, a ghost, a witch's hat — gridded with symbols, black on white, in three styles."},
            {q: 'Does the chart include a colour key?', a: 'Each chart marks stitch symbols on the grid; matching symbols to specific thread colours is a step you add based on your own palette.'},
            {q: 'What fabric count does this pattern assume?', a: 'The chart is a symbol grid, not tied to one fabric count — the finished size depends on the aida count you stitch it on.'},
            {q: 'Can I get a different subject, like a black cat or bat?', a: 'Yes — the direction field takes any Halloween subject, including a black cat or a bat.'},
            {q: 'Can I sell finished pieces stitched from this chart?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
